import { useState, useMemo } from 'react'
import {
  Badge,
  Button,
  Card,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { BarChart } from '@mantine/charts'
import { IconDownload, IconRefresh } from '@tabler/icons-react'
import { useQaErrorRate } from '@/hooks/useAnalytics'
import { QaErrorRateResponse } from '@/services/analytics.api'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()

const PERIOD_OPTIONS = [
  { label: '7 days', value: '7' },
  { label: '14 days', value: '14' },
  { label: '30 days', value: '30' },
  { label: '60 days', value: '60' },
  { label: '90 days', value: '90' },
]

function MetricCard({ label, value, subtitle }: { label: string; value: string | number; subtitle?: string }) {
  return (
    <Paper p="md" radius="md" withBorder>
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">{label}</Text>
      <Text size="xl" fw={700} mt={4}>{value}</Text>
      {subtitle && <Text size="xs" c="dimmed" mt={2}>{subtitle}</Text>}
    </Paper>
  )
}

function escapeCsvField(val: string | null | undefined): string {
  if (val == null) return ''
  const s = String(val)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

function downloadCsv(rows: QaErrorRateResponse['rows']) {
  const headers = [
    'search_id', 'court_search_id', 'offense_id',
    'charge', 'disposition', 'charge_date', 'case_number', 'jurisdiction', 'jurisdiction_state',
    'pre_do_not_report', 'pre_result', 'pre_reportability',
    'post_do_not_report', 'post_result', 'post_reportability',
    'pre_notes', 'post_notes',
    'received_date', 'pickup_date', 'final_qa_date', 'searched_date',
    'time_to_pickup_hours', 'time_to_qa_hours', 'processing_turnaround_hours',
    'compared_at',
  ]
  const csvRows = [headers.join(',')]
  for (const r of rows) {
    csvRows.push([
      r.searchId, r.courtSearchId ?? '', r.offenseId,
      escapeCsvField(r.charge), escapeCsvField(r.disposition), r.chargeDate ?? '',
      r.caseNumber ?? '', escapeCsvField(r.jurisdiction), r.jurisdictionState ?? '',
      r.preDoNotReport ?? '', r.preResult ?? '', r.preReportability,
      r.postDoNotReport ?? '', r.postResult ?? '', r.postReportability,
      escapeCsvField(r.preNotes), escapeCsvField(r.postNotes),
      r.receivedDate ?? '', r.pickupDate ?? '', r.finalQaDate ?? '', r.searchedDate ?? '',
      r.timeToPickupHours ?? '', r.timeToQaHours ?? '', r.processingTurnaroundHours ?? '',
      r.comparedAt,
    ].join(','))
  }
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `qa_error_rate_mismatches.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export default function QaErrorRateDashboard() {
  const [days, setDays] = useState(30)
  const [page, setPage] = useState(1)
  const pageSize = 50

  const { data: qaData, isLoading, refetch } = useQaErrorRate(days, page, pageSize)

  const trendChartData = useMemo(() => {
    if (!qaData?.trend) return []
    return qaData.trend.map((t) => ({
      date: t.date,
      'Error Rate (%)': t.rate,
      'Total Comparisons': t.total,
      'Decisions Changed': t.changed,
    }))
  }, [qaData?.trend])

  if (isLoading && !qaData) {
    return (
      <Center h="60vh">
        <Loader size="xl" />
      </Center>
    )
  }

  if (!qaData) {
    return (
      <Container fluid p="md">
        <Center h="40vh">
          <Stack align="center" gap="md">
            <Text size="lg" c="dimmed">No QA error rate data available.</Text>
            <Button onClick={() => refetch()} leftSection={<IconRefresh size={16} />}>Refresh</Button>
          </Stack>
        </Center>
      </Container>
    )
  }

  const { metrics, rows, totalMismatchRows } = qaData
  const totalPages = Math.ceil(totalMismatchRows / pageSize)

  return (
    <Container fluid p="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Title order={2}>QA Error Rate Dashboard</Title>
          <Group gap="sm">
            <SegmentedControl
              value={String(days)}
              onChange={(val) => { setDays(Number(val)); setPage(1) }}
              data={PERIOD_OPTIONS}
              size="sm"
            />
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Summary Metrics */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MetricCard label="Total Comparisons" value={fmt(metrics.totalComparisons)} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MetricCard
              label="Error Rate"
              value={`${metrics.errorRate}%`}
              subtitle={`${fmt(metrics.decisionsChanged)} decisions changed`}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MetricCard
              label="Direction Breakdown"
              value={`${metrics.reportableToNotReportable} R→NR`}
              subtitle={`${metrics.notReportableToReportable} NR→R`}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MetricCard
              label="Unique Cases"
              value={fmt(metrics.uniqueCases)}
              subtitle={`${metrics.periodDays}-day period`}
            />
          </Grid.Col>
        </Grid>

        {/* Time Metrics */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <MetricCard
              label="Avg Time to Pickup"
              value={metrics.avgTimeToPickupHours != null ? `${metrics.avgTimeToPickupHours}h` : 'N/A'}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <MetricCard
              label="Avg Time to QA"
              value={metrics.avgTimeToQaHours != null ? `${metrics.avgTimeToQaHours}h` : 'N/A'}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <MetricCard
              label="Avg Processing Turnaround"
              value={metrics.avgProcessingTurnaroundHours != null ? `${metrics.avgProcessingTurnaroundHours}h` : 'N/A'}
            />
          </Grid.Col>
        </Grid>

        {/* Error Rate Trend */}
        {trendChartData.length > 0 && (
          <Card withBorder p="lg">
            <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="md">Daily Error Rate Trend</Text>
            <BarChart
              h={300}
              data={trendChartData}
              dataKey="date"
              series={[
                { name: 'Error Rate (%)', color: 'red.6' },
              ]}
              tickLine="y"
              gridAxis="y"
            />
          </Card>
        )}

        {/* Mismatch Detail Table */}
        <Card withBorder p="lg">
          <Group justify="space-between" mb="md">
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">
              Mismatch Details ({fmt(totalMismatchRows)} rows)
            </Text>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconDownload size={14} />}
              onClick={() => downloadCsv(rows)}
            >
              Export CSV
            </Button>
          </Group>
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Search ID</Table.Th>
                  <Table.Th>Court Search ID</Table.Th>
                  <Table.Th>Offense ID</Table.Th>
                  <Table.Th>Charge</Table.Th>
                  <Table.Th>Disposition</Table.Th>
                  <Table.Th>Jurisdiction</Table.Th>
                  <Table.Th>Pre DoNotReport</Table.Th>
                  <Table.Th>Pre Result</Table.Th>
                  <Table.Th>Pre Decision</Table.Th>
                  <Table.Th>Post DoNotReport</Table.Th>
                  <Table.Th>Post Result</Table.Th>
                  <Table.Th>Post Decision</Table.Th>
                  <Table.Th>Pre Notes</Table.Th>
                  <Table.Th>Post Notes</Table.Th>
                  <Table.Th>Pickup Date</Table.Th>
                  <Table.Th>Compared At</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r, i) => (
                  <Table.Tr key={`${r.searchId}-${r.offenseId}-${i}`}>
                    <Table.Td><Text size="sm" ff="monospace">{r.searchId}</Text></Table.Td>
                    <Table.Td><Text size="sm" ff="monospace">{r.courtSearchId ?? '-'}</Text></Table.Td>
                    <Table.Td><Text size="sm" ff="monospace">{r.offenseId}</Text></Table.Td>
                    <Table.Td><Text size="xs" lineClamp={2}>{r.charge ?? '-'}</Text></Table.Td>
                    <Table.Td><Text size="xs" lineClamp={2}>{r.disposition ?? '-'}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.jurisdiction ?? '-'}{r.jurisdictionState ? ` (${r.jurisdictionState})` : ''}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.preDoNotReport != null ? String(r.preDoNotReport) : '-'}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.preResult ?? '-'}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={r.preReportability === 'REPORTABLE' ? 'blue' : 'gray'} variant="light" size="sm">
                        {r.preReportability}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs">{r.postDoNotReport != null ? String(r.postDoNotReport) : '-'}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.postResult ?? '-'}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={r.postReportability === 'REPORTABLE' ? 'blue' : 'gray'} variant="light" size="sm">
                        {r.postReportability}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        label={r.preNotes ?? '-'}
                        multiline
                        maw={500}
                        withArrow
                        position="left"
                        styles={{ tooltip: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }}
                      >
                        <Text size="xs" lineClamp={1} maw={200} style={{ cursor: 'pointer' }}>{r.preNotes ?? '-'}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        label={r.postNotes ?? '-'}
                        multiline
                        maw={500}
                        withArrow
                        position="left"
                        styles={{ tooltip: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }}
                      >
                        <Text size="xs" lineClamp={1} maw={200} style={{ cursor: 'pointer' }}>{r.postNotes ?? '-'}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td><Text size="xs">{r.pickupDate ? new Date(r.pickupDate).toLocaleDateString() : '-'}</Text></Table.Td>
                    <Table.Td><Text size="xs">{new Date(r.comparedAt).toLocaleDateString()}</Text></Table.Td>
                  </Table.Tr>
                ))}
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={16}>
                      <Center py="md"><Text c="dimmed">No mismatches found</Text></Center>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination total={totalPages} value={page} onChange={setPage} />
            </Group>
          )}
        </Card>
      </Stack>
    </Container>
  )
}
